import Fragment from "./fragment";
import FragmentDataType from "../enums/fragment-data-type";
import { teamReducer } from "../../../storage/reducers";
import {
    getPeriodSummary,
    getTimeoutNumber,
    isOvertimePeriod
} from "../utils/context-utils";
import { isOvertimeTimed } from "../utils/overtime-utils";
import FragmentSummary from "./fragment-summary";

export default class Timeout extends Fragment {
    constructor(team, time) {
        super();

        this.team = team;
        this.time = time;

        this.requiredData = [
            {
                name: "team",
                type: FragmentDataType.Team,
                displayName: "Team",
                title: "Team",
                subtitle: "Select the team"
            },
            {
                name: "time",
                type: FragmentDataType.Time,
                displayName: "Time",
                title: "Time",
                subtitle: "Set the time the timeout was taken",
                isEditable: true,
                // An untimed overtime keeps no clock (PSTATS-1268): no other
                // play asks for a time there, and the UI's picker is disabled,
                // so the step is neither shown nor waited for.
                isSkipped: (play) => this.inUntimedOvertime(play)
            }
        ];
    }

    /**
     * Whether the play is in an overtime that OT Time Control leaves untimed,
     * read from the play's own snapshot (PeriodStart sets fbOtDisabled as an
     * overtime period starts). A play-free call fails safe to asking.
     * @param {FootballPlay} play
     * @returns {boolean}
     */
    inUntimedOvertime(play) {
        return !!play?.contextSnapshot?.fbOtDisabled;
    }

    excludeRequiredData(rd, gameConfig, play) {
        return rd.name === "time" && this.inUntimedOvertime(play);
    }

    summarize(summaryContext, stringifier, play) {
        // A timeout in an untimed overtime carries no time; its period is
        // the play's.
        const { period = play?.contextSnapshot?.currentPeriod, milliseconds } =
            this.time || {};
        const maxPeriods =
            summaryContext.gameConfig.general.periodsInRegulation;

        const timeoutNumber = getTimeoutNumber(
            this.team?.id,
            period,
            summaryContext
        );
        const { period: normalizedPeriod, abbreviation } = getPeriodSummary(
            period,
            maxPeriods
        );

        // An untimed overtime keeps no clock, so its timeouts name no time
        // (PSTATS-1268), as its period and drive starts do not.
        const untimedOvertime =
            isOvertimePeriod(period, maxPeriods) &&
            !isOvertimeTimed(summaryContext.gameConfig);
        const time = untimedOvertime
            ? ""
            : `, at ${stringifier.milisecondsToMMSS(milliseconds)}`;

        const message = `${stringifier.ordinalSuffix(
            timeoutNumber + 1
        )} Timeout taken by ${
            this.team?.teamName
        }${time} in the ${stringifier.ordinalSuffix(
            normalizedPeriod
        )} ${abbreviation}.`;

        return new FragmentSummary(message);
    }

    updateSummaryContext(summaryContext) {
        summaryContext.eopPrinted = true;
    }

    toString() {
        return `${super.toString()}: for ${
            this.team.teamName
        } at ${this.time.toLocaleTimeString()}`;
    }

    toJSON() {
        return {
            ...super.toJSON(),
            team: teamReducer(this.team, "teamName")
        };
    }
}
